import { useEffect, useRef, useState } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ShieldCheck, Clock, HeartHandshake } from "lucide-react";
import api from "@/lib/api";
import AnimatedNumber from "@/components/AnimatedNumber";

const HERO_IMG =
  "https://images.unsplash.com/photo-1604323990536-e5452c0507c1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjV8MHwxfHNlYXJjaHwxfHxmcmVzaCUyMGZsb3dlciUyMGJhc2tldCUyMGRlbGl2ZXJ5fGVufDB8fHx8MTc4Njg3OTkxOHww&ixlib=rb-4.1.0&q=85";

const TITLE_LINES = ["Vos courses, vos colis,", "vos fleurs — livrés chez", "vous, avec le cœur."];

const CHIPS = [
  { icon: ShieldCheck, label: "Micro-entreprise locale & assurée" },
  { icon: Clock, label: "Lun–Ven 8h–17h · Sam 8h–16h" },
  { icon: HeartHandshake, label: "Un interlocuteur unique, humain" },
];

const ease = [0.22, 1, 0.36, 1];

const BASE_LIVRAISONS = 250; // livraisons réalisées via Tutut avant le site
const BASE_KM = 361.51; // kilomètres parcourus via Shopopop avant le site

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 90]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, -40]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api
      .get("/public-stats")
      .then(({ data }) => setStats(data))
      .catch(() => setStats({ livrees: 0, km: 0 }));
  }, []);

  return (
    <section ref={ref} id="haut" className="grain relative overflow-hidden pb-20 pt-32 md:pb-28 md:pt-40">
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-12 lg:gap-10 lg:px-8">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <motion.span
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, ease }}
            className="mb-6 inline-flex w-fit items-center rounded-full bg-forest/10 px-4 py-1.5 text-xs font-semibold uppercase tracking-[0.18em] text-forest"
            data-testid="hero-eyebrow"
          >
            Livraison de proximité à Illzach & alentours
          </motion.span>

          <h1 className="font-serif text-4xl font-semibold leading-[1.06] tracking-tight text-ink sm:text-5xl lg:text-6xl">
            {TITLE_LINES.map((line, i) => (
              <span key={line} className="block overflow-hidden pb-1">
                <motion.span
                  className="block"
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.18 + i * 0.14, ease }}
                >
                  {i === 1 ? (
                    <>
                      vos fleurs — <em className="font-medium italic text-terra">livrés chez</em>
                    </>
                  ) : (
                    line
                  )}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.7, ease }}
            className="mt-6 max-w-xl text-base leading-relaxed text-ink/70 sm:text-lg"
          >
            Fini les applications impersonnelles. Angélique, votre livreuse indépendante à Illzach, récupère
            vos drives, vos colis et vos bouquets, et les apporte d'un point A à un point B — pour vous, vos
            parents ou vos proches qui ne peuvent plus se déplacer.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85, ease }}
            className="mt-9 flex flex-wrap items-center gap-4"
          >
            <a
              href="#reserver"
              data-testid="hero-booking-cta"
              className="group flex min-h-[52px] items-center gap-2 rounded-full bg-forest px-7 text-base font-semibold text-cream transition-all duration-200 hover:-translate-y-0.5 hover:bg-forest-dark"
            >
              Prendre rendez-vous
              <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
            </a>
            <a
              href="#fonctionnement"
              className="flex min-h-[52px] items-center rounded-full border border-ink/15 px-7 text-base font-semibold text-ink transition-colors duration-200 hover:border-forest hover:text-forest"
            >
              Comment ça marche
            </a>
          </motion.div>

          <motion.ul
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.05 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {CHIPS.map(({ icon: Icon, label }) => (
              <li key={label} className="flex items-center gap-2 text-sm font-medium text-ink/60">
                <Icon className="h-4 w-4 text-terra" />
                {label}
              </li>
            ))}
          </motion.ul>
        </motion.div>

        <div className="lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 30 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            transition={{ duration: 1, delay: 0.5, ease }}
            className="relative"
          >
            <motion.div
              initial={{ opacity: 0, scale: 0.6, rotate: -12 }}
              animate={{ opacity: 1, scale: 1, rotate: 0 }}
              transition={{ duration: 0.9, delay: 0.9, ease }}
              className="absolute -top-10 -left-4 z-10 sm:-left-8"
            >
              <img
                src="/logo-512.png"
                alt="Angélivraisons — Services & livraisons"
                className="h-24 w-24 rounded-full object-cover shadow-xl shadow-ink/20 ring-4 ring-cream sm:h-28 sm:w-28"
                data-testid="hero-logo-medallion"
              />
            </motion.div>
            <div className="absolute -left-6 -top-6 h-28 w-28 rounded-tl-[4rem] border-l-2 border-t-2 border-terra/50" aria-hidden="true" />
            <motion.div style={{ y: imgY }} className="overflow-hidden rounded-t-[10rem] rounded-b-[2rem] shadow-2xl shadow-forest/20">
              <img
                src={HERO_IMG}
                alt="Panier de fleurs fraîches prêt à être livré à domicile"
                className="aspect-[4/5] w-full object-cover"
                data-testid="hero-image"
              />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 1.1, ease }}
              className="absolute -bottom-6 -left-4 rounded-2xl bg-white px-5 py-4 shadow-xl shadow-ink/10 sm:-left-10"
            >
              {stats === null ? (
                <p className="font-serif text-3xl font-semibold text-forest">…</p>
              ) : (
                <div className="flex items-center gap-5">
                  <div>
                    <p className="font-serif text-3xl font-semibold text-forest" data-testid="deliveries-counter">
                      <AnimatedNumber value={BASE_LIVRAISONS + stats.livrees} />
                      <span className="text-terra">+</span>
                    </p>
                    <p className="text-sm font-medium text-ink/60">livraisons réalisées</p>
                  </div>
                  <div className="h-10 w-px bg-ink/10" aria-hidden="true" />
                  <div>
                    <p className="font-serif text-3xl font-semibold text-forest" data-testid="km-counter">
                      <AnimatedNumber value={Math.round(BASE_KM + stats.km)} />
                    </p>
                    <p className="text-sm font-medium text-ink/60">km parcourus</p>
                  </div>
                </div>
              )}
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
}
